import LockIcon from '@mui/icons-material/Lock';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import PopoverMaterial from '@mui/material/Popover';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import Chip from '/@/components/nitrogen-smart/chip';
import Popover from '/@/components/popover';
import colors from '/@/components/theme-provider/colors';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { _ } from '/@/locale';
import { updateNSRecommendationFromAPI } from '/@/store/nitrogen-recommendation/apiProviders';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { formatDate } from '/@/utils';
import { downloadFile } from '/@/utils/download-files';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';

const NewNitrogenRecoCard = ({ recommendation }) => {
    const history = useNavigate();
    const isMobile = useIsMobile();
    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();
    const id = open ? 'simple-popover' : undefined;

    const status = [
        {
            id: 'pending',
            color: colors.gray[200],
            component: <Chip label={_('PENDIENTE')} labelColor={colors.gray.default} />,
        },
        {
            id: 'available',
            color: colors.success[100],
            component: <Chip label={_('DISPONIBLE')} labelColor={colors.success[200]} bgcolor={colors.success[100]} />,
        },
        {
            id: 'processing',
            color: colors.warning[200],
            component: (
                <Chip
                    icon={<LockIcon />}
                    label={_('PROCESANDO')}
                    labelColor={colors.themes.dekalb.primary.default}
                    bgcolor={colors.warning[200]}
                    iconClasses={{
                        fontSize: 11,
                        color: colors.themes.dekalb.primary.default,
                    }}
                />
            ),
        },
    ];
    const getStatus = (recoStatus) => {
        return status.find((st) => {
            return st.id === recoStatus;
        });
    };

    const parseDate = (date) => {
        return formatDate(new Date(date));
    };

    const StatusComponent = () => getStatus(recommendation.status).component;

    const handleEditReco = () => {
        if (!recommendation.applicationDate) {
            const nitrogenRecommendationData = {
                ...recommendation,
                applicationDate: recommendation.estimatedApplicationDate,
            };

            updateNSRecommendationFromAPI({
                ...nitrogenRecommendationData,
            });

            enqueueSnackbar(_('La fecha de re-fertilización se ha modificado correctamente.'), {
                variant: 'success',
            });
        } else {
            mpTrackEvent('N-Smart Recommendation date confirmed', {
                entry_source: 'nitrogen-recommendation-list-alert',
            });
            enqueueSnackbar(_('Se ha confirmado la fecha de re-fertilización'), {
                variant: 'success',
            });
        }

        handleClose();
    };
    const today = new Date().setHours(0, 0, 0, 0);
    const refertilization = new Date(
        recommendation.applicationDate ? recommendation.applicationDate : recommendation.estimatedApplicationDate
    ).setHours(0, 0, 0, 0);
    const daysToRefertilization = () => {
        const difference = refertilization - today;
        const totalDays = Math.ceil(difference / (1000 * 3600 * 24));
        return totalDays;
    };

    function downloadFiles(files) {
        files.forEach((e) => {
            fetch(e.file)
                .then((res) => res.blob())
                .then((blob) => {
                    downloadFile(blob, e.name);
                });
        });
    }

    const redirectToRecoEdit = (step = undefined) => {
        history(
            reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                qs: { id: recommendation.id, step },
            })
        );
    };

    const showAlert = daysToRefertilization() > 0 && daysToRefertilization() < 15; // TODO: CHECK IF ITS OK
    return (
        <Box
            sx={{
                backgroundColor: 'white !important',
                height: 300,
                padding: 2,
                borderRadius: 2,
                width: '100%',
                border: `1px solid ${colors.gray[400]}`,
            }}
        >
            <Grid container direction='column' justifyContent='space-between' sx={{ height: '100%' }}>
                <Grid item container justifyContent='space-between'>
                    <Grid
                        item
                        container
                        sx={{
                            width: 'fit-content',
                        }}
                    >
                        <StatusComponent />
                    </Grid>
                    {recommendation.status === 'pending' && showAlert && (
                        <div
                            style={{
                                cursor: 'pointer',
                                borderRadius: '50%',
                                width: 25,
                                height: 25,
                                backgroundColor: colors.cropMonitoring.redPoint,
                                border: '4px solid #FF9999',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onClick={handleClick}
                        >
                            <span className='material-icons' style={{ fontSize: 10, color: 'white' }}>
                                {open ? 'close' : 'priority_high'}
                            </span>
                        </div>
                    )}
                </Grid>
                <Grid item sx={{ fontWeight: 800 }}>
                    {recommendation.fieldName}
                </Grid>

                <Grid
                    item
                    container
                    sx={{ border: `1px solid ${colors.gray[400]}`, borderRadius: 2, p: 2, fontSize: 12 }}
                >
                    <Grid item container direction='column' xs={6} sx={{ gap: 0.5 }}>
                        <Grid item>{_('Fecha de siembra')}</Grid>
                        <Grid item sx={{ fontWeight: 800 }}>
                            {parseDate(recommendation.plantingDate)}
                        </Grid>
                    </Grid>
                    <Grid item container direction='column' xs={6} sx={{ gap: 0.5 }}>
                        <Grid item>{_('Híbrido')}</Grid>
                        <Grid item sx={{ fontWeight: 800 }}>
                            {recommendation.productName}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    container
                    sx={{
                        border: `1px solid ${colors.gray[400]}`,
                        borderRadius: 2,
                        p: 2,
                        fontSize: 12,
                        position: 'relative',
                    }}
                >
                    <p
                        style={{
                            position: 'absolute',
                            backgroundColor: 'white',
                            fontSize: 10,
                            top: -7,
                            margin: 0,
                        }}
                    >
                        {_('Refertilización')}
                    </p>
                    <Grid item container direction='column' xs={6} sx={{ gap: 0.5 }}>
                        <Grid item>{_('Fecha estimada')}</Grid>
                        <Grid item sx={{ fontWeight: 800 }}>
                            {parseDate(recommendation.estimatedApplicationDate)}
                        </Grid>
                    </Grid>
                    <Grid item container direction='column' xs={6} sx={{ gap: 0.5 }}>
                        <Grid item>{_('Fecha definitiva')}</Grid>
                        <Grid item sx={{ fontWeight: 800 }}>
                            {recommendation.applicationDate ? parseDate(recommendation.applicationDate) : '-'}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item container>
                    <Grid item xs={recommendation.status === 'processing' ? 12 : 6} sx={{ pr: 0.5 }}>
                        <Button
                            variant='outlined'
                            fullWidth
                            onClick={() =>
                                history(
                                    reverse('nitrogenRecommendation.NitrogenRecommendationDetail', {
                                        params: { id: recommendation.id },
                                    })
                                )
                            }
                        >
                            {_('Ver detalle')}
                        </Button>
                    </Grid>
                    {recommendation.status !== 'processing' && (
                        <Grid item xs={6} sx={{ pl: 0.5 }}>
                            <Button
                                fullWidth
                                onClick={
                                    recommendation.status === 'available'
                                        ? () => {
                                              downloadFiles(
                                                  recommendation.results.map((r) => {
                                                      return {
                                                          file: r.file,
                                                          name: _('Informe de recomendación de nitrógeno'),
                                                      };
                                                  })
                                              );
                                          }
                                        : () => {
                                              history(
                                                  reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                                                      qs: { id: recommendation.id },
                                                  })
                                              );
                                          }
                                }
                                variant='contained'
                            >
                                {recommendation.status === 'available' ? _('Descargar informe') : _('Editar')}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            <PopoverMaterial
                id={id}
                open={open && !isMobile}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        borderRadius: 0,
                        marginTop: 10,
                    },
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        backgroundColor: 'white',
                        p: 4,
                        boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px;',
                        maxWidth: 350,
                    }}
                >
                    <Grid container direction='column' sx={{ gap: 2 }}>
                        <Grid item>
                            <Title size='lg' bolder>
                                {_('Fecha de re-fertilización')}
                            </Title>
                        </Grid>
                        <Grid item>
                            <Title size='sm'>
                                {`${_(
                                    'Está por llegar la fecha de re-fertilización indicada, en '
                                )} ${daysToRefertilization()} ${_(
                                    'días comenzaremos a procesar tus datos y ya no podrás modificar este campo ya sea estimado o definitivo.'
                                )}`}
                            </Title>
                        </Grid>
                        <Grid item container direction='column' sx={{ gap: 1 }}>
                            <Grid item>
                                <Button variant='contained' size='small' fullWidth onClick={() => handleEditReco()}>
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 5,
                                            margin: 0,
                                            text: 'color',
                                        }}
                                    >
                                        <span
                                            className='material-icons'
                                            style={{ color: colors.success[200], fontSize: 14 }}
                                        >
                                            done
                                        </span>
                                        <span>
                                            {`${_('Sí, voy a re-fertilizar el día')} ${
                                                recommendation.applicationDate
                                                    ? parseDate(recommendation.applicationDate)
                                                    : parseDate(recommendation.estimatedApplicationDate)
                                            }`}
                                        </span>
                                    </span>
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button variant='outlined' size='small' fullWidth onClick={() => redirectToRecoEdit(2)}>
                                    {_('Quiero modificar la fecha de re-fertilización')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </PopoverMaterial>

            <Popover title={_('Fecha de re-fertilización')} open={open && isMobile} handleClose={handleClose}>
                <Grid item>
                    <Title size='sm'>
                        {`${_(
                            'Está por llegar la fecha de re-fertilización indicada, en '
                        )} ${daysToRefertilization()} ${_(
                            'días comenzaremos a procesar tus datos y ya no podrás modificar este campo ya sea estimado o definitivo.'
                        )}`}
                    </Title>
                </Grid>
                <Grid item container direction='column' sx={{ gap: 1 }}>
                    <Grid item>
                        <Button variant='contained' fullWidth onClick={() => handleEditReco()}>
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    margin: 0,
                                    text: 'color',
                                }}
                            >
                                <span className='material-icons' style={{ color: colors.success[200], fontSize: 14 }}>
                                    done
                                </span>
                                <span>
                                    {`${_('Sí, voy a re-fertilizar el día')} ${
                                        recommendation.applicationDate
                                            ? parseDate(recommendation.applicationDate)
                                            : parseDate(recommendation.estimatedApplicationDate)
                                    }`}
                                </span>
                            </span>
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant='outlined' fullWidth onClick={() => redirectToRecoEdit(2)}>
                            {_('Quiero modificar la fecha de re-fertilización')}
                        </Button>
                    </Grid>
                </Grid>
            </Popover>
        </Box>
    );
};

export default NewNitrogenRecoCard;
